import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "../../core/translation.core";
import useShop from "../../hooks/useShop.hook";
import { Product } from "../../types/product";
import PencilIcon from "../../icons/pencil.icon";
import Link from "next/link";
import DeleteIcon from "../../icons/delete.icon";
import DeleteProductDialog from "./deleteProductDialog.comp";
import { formatCurrency } from "../../core/currency.core";
import { LocaleText } from "../../types/locale";
import localeCore from "../../core/locale.core";
import { inputCss } from "../reuseables/native.comp";
import XMarkIcon from "../../icons/xMark.icon";
import CheckIcon from "../../icons/check.icon";
import { useForm } from "react-hook-form";
import { post } from "../../core/fetch.core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reOrderVariations } from "../../core/product.core";

export default function ChildProductList({ parentProduct }: { parentProduct: Product }) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const reOrderMutation = useMutation({
    mutationFn: async (variationIds: string[]) => {
      await reOrderVariations(parentProduct.id, variationIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productSingle", parentProduct.id] });
    },
  });

  if (!parentProduct.variations) return null;

  return (
    <div id="child-list">
      <div className="w-full grid grid-cols-5 items-center gap-y-2">
        <div className="text-left mb-2 font-semibold"></div>
        <div className="text-left mb-2 font-semibold">{t(msg.product.sku)}</div>
        <div className="text-left mb-2 font-semibold">{t(msg.attribute.attribute)}</div>
        <div className="text-right mb-2 font-semibold">{t(msg.product.price2)}</div>
        <div className="mb-2"></div>
      </div>
      {parentProduct.variations.map((v, idx) => (
        <ChildAttributeRow
          key={v.id}
          variation={v}
          parentProduct={parentProduct}
          idx={idx}
          reOrder={reOrderMutation}
          variations={parentProduct.variations}
        />
      ))}
      {parentProduct.variations.length > 0 && (
        <DropDiv dragIdx={parentProduct.variations.length} reOrder={reOrderMutation} variations={parentProduct.variations} />
      )}
    </div>
  );
}

interface ChildAttributeRowProps {
  variation: Product;
  parentProduct: Product;
  idx: number;
  reOrder: ReturnType<typeof useMutation>;
  variations: Product[];
}

function ChildAttributeRow({ variation, parentProduct, idx, reOrder, variations }: ChildAttributeRowProps) {
  const { selectedShop } = useShop();

  const dialogRef = useRef<HTMLDialogElement>(null);

  function drag(ev: React.DragEvent<HTMLDivElement>) {
    ev.dataTransfer.setData("id", variation.id);
    ev.dataTransfer.setData("idx", idx.toString());
    (window as any).draggingVariation = { variation, srcIdx: idx };
  }

  async function clickDelete() {
    dialogRef.current?.showModal();
  }

  async function clickCancel() {
    dialogRef.current?.close();
  }
  if (!selectedShop) return null;

  return (
    <>
      <DropDiv dragIdx={idx} reOrder={reOrder} variations={variations} />
      <div
        className="child-list-row w-full grid grid-cols-5 items-center gap-y-2 border-t py-2 min-h-[69px] cursor-move"
        draggable="true"
        onDragStart={drag}
        onDragEnd={() => {
          (window as any).draggingVariation = null;
        }}
      >
        <div>#{idx + 1}</div>
        <div>{variation.sku}</div>
        <div>
          {variation.attributes.map((a) => (
            <div key={a.attributeId}>
              {a.name}: {getValue(a.value)}
            </div>
          ))}
        </div>
        <PriceCell variation={variation} />
        <div>
          <div className="flex items-center gap-3 justify-end">
            <Link
              className="underline edit-child"
              href={`/product/child-edit?variationId=${variation.id}&parentId=${parentProduct.id}`}
            >
              <PencilIcon className="w-5 h-5" />
            </Link>
            <DeleteIcon className="w-6 h-6 cursor-pointer text-red-400" onClick={clickDelete} />
          </div>
          <DeleteProductDialog dialogRef={dialogRef} productId={variation.id} clickCancelled={clickCancel} />
        </div>
      </div>
    </>
  );
}

interface DropDivProps {
  dragIdx: number;
  reOrder: ReturnType<typeof useMutation>;
  variations: Product[];
}

const DropDiv = ({ dragIdx, reOrder, variations }: DropDivProps) => {
  const [onDrag, setOnDrag] = useState(false);

  function drop(ev: React.DragEvent<HTMLDivElement>) {
    setOnDrag(false);
    ev.preventDefault();
    if (allowDrag()) {
      const variationId = ev.dataTransfer.getData("id");

      const reorderedIds = variations.filter((v: Product) => v.id !== variationId).map((v: Product) => v.id);

      reorderedIds.splice(dragIdx, 0, variationId);

      reOrder.mutateAsync(reorderedIds);
    }

    (window as any).draggingVariation = null;
  }

  function onDragOver(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
    setOnDrag(allowDrag());
  }

  function allowDrag() {
    const draggingVariation = (window as any).draggingVariation;
    if (!draggingVariation) return false;

    const { srcIdx } = draggingVariation;
    if (srcIdx === dragIdx || srcIdx === dragIdx - 1) return false;

    return true;
  }

  return (
    <div onDrop={drop} onDragLeave={() => setOnDrag(false)} onDragOver={onDragOver} className="p-2">
      {onDrag && <div className="h-[50px] border border-dashed rounded-lg bg-gray-400 dark:bg-gray-500"></div>}
    </div>
  );
};

function getValue(value: LocaleText | number) {
  if (localeCore.isNumber(value)) return value.toString();
  return localeCore.getLocaleValueDefault(value as LocaleText);
}

function PriceCell({ variation }: { variation: Product }) {
  const { selectedShop } = useShop();
  const [isEditing, setIsEditing] = useState(false);

  if (!selectedShop) return null;

  if (isEditing) {
    return <EditPriceCell product={variation} isEditing={isEditing} setIsEditing={setIsEditing} />;
  }

  const price = variation.price + (variation.recycleDeposit ?? 0);

  return (
    <div data-price={price} className="child-list-row-price flex justify-end items-center gap-2 group">
      {formatCurrency(price, selectedShop.defaultCurrency as any)}
      <button type="button" className="hidden group-hover:block hover:text-her-dodgerblue" onClick={() => setIsEditing(true)}>
        <PencilIcon className="w-5 h-5" />
      </button>
    </div>
  );
}

function EditPriceCell({
  product,
  isEditing,
  setIsEditing,
}: {
  product: Product;
  isEditing: boolean;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
}) {
  const methods = useForm<{ price: number }>({
    defaultValues: {
      price: product.price ?? 0,
    },
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isEditing) methods.setFocus("price");
  }, [isEditing, methods]);

  async function submit() {
    await post("/admin/product/updatePrice", {
      productId: product.id,
      price: methods.getValues().price,
    });

    queryClient.invalidateQueries({ queryKey: ["productSingle", product.id] });
    if (product.parentId) {
      queryClient.invalidateQueries({ queryKey: ["productSingle", product.parentId] });
    }

    setIsEditing(false);
  }

  return (
    <div className="flex justify-end items-center gap-2">
      <input
        className={`${inputCss} w-full`}
        type="number"
        step=".01"
        {...methods.register("price")}
        onKeyUp={(e) => {
          if (e.keyCode === 13) {
            submit();
          }
        }}
      />
      <button type="button" onClick={() => setIsEditing(false)}>
        <XMarkIcon className="w-5 h-5" />
      </button>
      <button type="button" onClick={submit}>
        <CheckIcon className="w-5 h-5" />
      </button>
    </div>
  );
}
